'use client'

import * as React from 'react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { cn } from '@/lib/utils'

const COLOR_MAP = {
  lime: 'var(--color-lime)',
  cyan: 'var(--color-cyan)',
  purple: 'var(--color-purple)',
  pink: 'var(--color-pink)',
} as const

export interface TrendChartSeries {
  dataKey: string
  color: keyof typeof COLOR_MAP
  label?: string
}

export interface TrendChartProps {
  data: Record<string, any>[]
  series: TrendChartSeries[]
  xAxisKey?: string
  xAxisFormatter?: (value: string) => string
  yAxisFormatter?: (value: number) => string
  tooltipLabelFormatter?: (value: string) => string
  tooltipValueFormatter?: (value: number, name: string) => React.ReactNode
  height?: number
  title?: string
  showGrid?: boolean
  showXAxis?: boolean
  showYAxis?: boolean
  /**
   * Optional fixed [min, max] for the Y axis. Defaults to recharts'
   * auto-scaling (unchanged behavior) when omitted — set this for metrics
   * with a known fixed range (e.g. a 0-100 score) where auto-scaling would
   * visually exaggerate small fluctuations.
   */
  yAxisDomain?: [number, number]
  /**
   * 'card' (default) wraps the chart in its own bordered surface; 'plain'
   * renders just the chart, for embedding inside an existing card (e.g. the
   * vitals hero band) without a border-in-border look.
   */
  variant?: 'card' | 'plain'
  className?: string
}

const TrendChart: React.FC<TrendChartProps> = ({
  data,
  series,
  xAxisKey = 'date',
  xAxisFormatter,
  yAxisFormatter,
  tooltipLabelFormatter,
  tooltipValueFormatter,
  height = 256,
  title,
  showGrid = true,
  showXAxis = true,
  showYAxis = true,
  yAxisDomain,
  variant = 'card',
  className,
}) => {
  if (!data.length || !series.length) return null

  return (
    <div
      className={cn(variant === 'card' && 'rounded-xl p-6', className)}
      style={variant === 'card' ? { background: 'var(--color-surface)', border: '1px solid var(--color-border)' } : undefined}
    >
      {title && (
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            {title}
          </h3>
          {series.length > 1 && (
            <div className="flex items-center gap-4">
              {series.map((s) => (
                <div key={s.dataKey} className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLOR_MAP[s.color] }} />
                  <span>{s.label || s.dataKey}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data}>
            <defs>
              {series.map((s) => (
                <linearGradient key={s.dataKey} id={`grad-${s.dataKey}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLOR_MAP[s.color]} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={COLOR_MAP[s.color]} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>

            {showGrid && (
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            )}

            {showXAxis && (
              <XAxis
                dataKey={xAxisKey}
                stroke="var(--color-text-muted)"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={xAxisFormatter}
              />
            )}

            {showYAxis && (
              <YAxis
                stroke="var(--color-text-muted)"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                tickFormatter={yAxisFormatter}
                domain={yAxisDomain}
              />
            )}

            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                color: 'var(--color-text-primary)',
                fontSize: '12px',
              }}
              labelStyle={{ color: 'var(--color-text-muted)', fontSize: '11px' }}
              labelFormatter={tooltipLabelFormatter}
              formatter={(value: number, name: string) => [
                tooltipValueFormatter ? tooltipValueFormatter(value, name) : value,
                series.find((s) => s.dataKey === name)?.label || name,
              ]}
            />

            {series.map((s) => (
              <Area
                key={s.dataKey}
                type="monotone"
                dataKey={s.dataKey}
                stroke={COLOR_MAP[s.color]}
                strokeWidth={2}
                fillOpacity={1}
                fill={`url(#grad-${s.dataKey})`}
                // A single-point series has no line segment to draw, so an
                // Area with the default dot={false} renders nothing visible.
                // Force a visible dot when there's exactly one point; leave
                // multi-point series unchanged (no dots, matches prior look).
                dot={data.length === 1 ? { r: 4, strokeWidth: 0, fill: COLOR_MAP[s.color] } : false}
                isAnimationActive={data.length > 1}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

export { TrendChart }
